import { BoolDisplayPipe } from '../../pipes/boolDisplay.pipe'
import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay, FilterMeta,
  QueryParam,
} from './common'
import { FilterAction } from './filter.service'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export type FlagFilterState = Readonly<{
  value: boolean | null,
  config: {
    meta: FilterMeta
    constant?: boolean
  }
}>

export const initialFlagFilterState: FlagFilterState = {
  value: null,
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}

export interface SetFlagFilterConfigAction {
  type: 'SET_FLAG_FILTER_CONFIG_ACTION',
  payload: Pick<FlagFilterState, 'config'>,
  meta: Omit<FlagFilterState['config']['meta'], 'fieldDisplay'>
}

export interface SetFlagFilterValueAction {
  type: 'SET_FLAG_FILTER_VALUE_ACTION',
  payload: Pick<FlagFilterState, 'value'>,
  meta: Omit<FlagFilterState['config']['meta'], 'fieldDisplay'>
}

export type FlagFilterAction =
  | SetFlagFilterConfigAction
  | SetFlagFilterValueAction

export const flagFilterReducer = (state: FlagFilterState, action: FlagFilterAction) => {
  switch (action.type) {
    case 'SET_FLAG_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_FLAG_FILTER_VALUE_ACTION':
      return {
        ...state,
        value: action.payload.value,
      }
    default:
      return state
  }
}

export class FlagFilter {
  public readonly type = 'FLAG'

  public static SetFlagFilterConfigAction(
    payload: SetFlagFilterConfigAction['payload'],
    meta: Omit<FlagFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetFlagFilterConfigAction { return { type: 'SET_FLAG_FILTER_CONFIG_ACTION', payload, meta } }
  public static SetFlagFilterValueAction(
    payload: SetFlagFilterValueAction['payload'],
    meta: Omit<FlagFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetFlagFilterValueAction { return { type: 'SET_FLAG_FILTER_VALUE_ACTION', payload, meta } }

  public state: FlagFilterState
  constructor(
    partialStateWithConfig: Partial<FlagFilterState> & Pick<FlagFilterState, 'config'>,
    private dispatch: (action: FilterAction) => void,
  ) {
    this.state = { ...initialFlagFilterState, ...partialStateWithConfig }
  }

  public apply(action: FlagFilterAction) {
    this.state = flagFilterReducer(this.state, action)
    return this
  }

  public asParams(): FilterParams[] {
    return this.state.value !== null ? [
      {
        name: this.state.config.meta.fieldName,
        values: [
          {
            value: `${this.state.value}`,
          },
        ],
      },
    ] : []
  }

  asActiveDisplay(): ActiveFilterDisplay[] {
    return this.state.value !== null
      ? [
        {
          headerName: this.state.config.meta.fieldDisplay,
          operation: '=',
          value: new BoolDisplayPipe().transform(this.state.value),
          constant: this.state.config.constant ?? false,
          remove: (() =>
            this.dispatch(FlagFilter.SetFlagFilterValueAction(
              { value: null },
              this.state.config.meta,
            ))
          ),
        },
      ]
      : []
  }

  asCount() {
    return this.state.value !== null ? 1 : 0
  }

  fromUrlQueryParams(
    params: readonly QueryParam[],
    defaultVal: boolean | null | undefined,
  ) {
    const qp = params.find((p) => p.name === this.state.config.meta.fieldName)
    if (qp?.value) {
      this.dispatch(FlagFilter.SetFlagFilterValueAction(
        { value: qp.value === 'true' },
        this.state.config.meta,
      ))
    }
    else if (defaultVal || defaultVal === false) {
      this.dispatch(FlagFilter.SetFlagFilterValueAction(
        { value: defaultVal },
        this.state.config.meta,
      ))
    }
  }

  asUrlQueryParams(): QueryParam[] {
    return this.state.value !== null
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: `${this.state.value}`,
        },
      ]
      : []
  }

  // noinspection JSUnusedGlobalSymbols
  asDashboardQueryParams(): QueryParam[] {
    return this.state.value !== null
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: `${this.state.value}`,
        },
      ]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    const allowed_fields = [
      'bmg_flag',
      'tie_flag',
      'usage_ingestion_block_flag',
    ]
    const field = this.state.config.meta.fieldName
    return allowed_fields.includes(field)
      ? { [field]: this.state.value }
      : {}
  }
}
